import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, CheckCircle2, AlertCircle, Send, MessageCircle, Instagram, Mail } from "lucide-react";
import { SITE_TYPES } from "../lib/content";
import { WHATSAPP, waLink, INSTAGRAM_URL } from "../lib/site";
import { Reveal, Eyebrow, MaskLines } from "./Reveal";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "";
const EMPTY = { nome: "", empresa: "", telefone: "", tipo_negocio: "", tipo_site: "", descricao: "", prazo: "", website: "" };

const validate = (f) => {
  const e = {};
  if (f.nome.trim().length < 2) e.nome = "Informe seu nome.";
  if (!f.empresa.trim()) e.empresa = "Informe o nome da empresa.";
  const d = f.telefone.replace(/\D/g, "");
  if (d.length < 10 || d.length > 13) e.telefone = "Informe um telefone com DDD (ex.: 11 91234-5678).";
  if (f.tipo_negocio.trim().length < 2) e.tipo_negocio = "Informe o tipo de negócio.";
  if (!f.tipo_site) e.tipo_site = "Selecione o tipo de site.";
  if (f.descricao.trim().length < 10) e.descricao = "Descreva o projeto em pelo menos 10 caracteres.";
  return e;
};

const buildMessage = (f) =>
  [
    "*Novo pedido de orçamento — JL Nexus*",
    "",
    `*Nome:* ${f.nome}`,
    `*Empresa:* ${f.empresa}`,
    `*Telefone/WhatsApp:* ${f.telefone}`,
    `*Tipo de negócio:* ${f.tipo_negocio}`,
    `*Tipo de site:* ${f.tipo_site}`,
    `*Prazo desejado:* ${f.prazo || "Não informado"}`,
    "",
    `*Descrição:* ${f.descricao}`,
  ].join("\n");

const inputCls = "w-full rounded-xl border bg-[#0B0F18] px-4 py-3.5 text-base text-white placeholder:text-slate-500 transition-[border-color,box-shadow] duration-300 focus:border-[#00C8FF] focus:outline-none focus:ring-2 focus:ring-[#00C8FF]/30";

const Field = ({ id, label, error, optional, children }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className="text-sm font-medium text-slate-200">
      {label} {optional ? <span className="text-slate-400">(opcional)</span> : <span className="text-[#00C8FF]">*</span>}
    </label>
    {children}
    {error && <p data-testid={`error-${id}`} className="text-sm text-red-400">{error}</p>}
  </div>
);

export const QuoteForm = () => {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const [wa, setWa] = useState("");

  useEffect(() => {
    const onPrefill = (e) => setF((p) => ({ ...p, tipo_negocio: e.detail || p.tipo_negocio }));
    window.addEventListener("jl:prefill", onPrefill);
    return () => window.removeEventListener("jl:prefill", onPrefill);
  }, []);

  const set = (k) => (e) => {
    setF((p) => ({ ...p, [k]: e.target.value }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };
  const cls = (k) => `${inputCls} ${errors[k] ? "border-red-400/70" : "border-white/10"}`;

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) {
      setStatus("error");
      setMsg("Revise os campos destacados.");
      return;
    }
    if (!API) {
      const link = waLink(buildMessage(f));
      setWa(link);
      if (link) window.open(link, "_blank", "noopener");
      setStatus("success");
      setMsg(link ? "Abrimos o WhatsApp com sua mensagem pronta — é só enviar!" : "Fale com a JL Nexus pelo Instagram ou e-mail para solicitar seu orçamento.");
      setF(EMPTY);
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/quotes`, { ...f, prazo: f.prazo || null, website: f.website || null });
      const link = waLink(buildMessage(f));
      setWa(link);
      if (link) window.open(link, "_blank", "noopener");
      setStatus("success");
      setMsg(link ? "Pedido registrado! Abrimos o WhatsApp com sua mensagem pronta — é só enviar." : "Pedido registrado com sucesso! A JL Nexus entrará em contato pelo telefone informado.");
      setF(EMPTY);
    } catch (err) {
      setStatus("error");
      setMsg(err?.response?.status === 429 ? "Muitas solicitações seguidas. Tente novamente em alguns minutos." : "Não foi possível enviar agora. Verifique sua conexão e tente novamente.");
    }
  };

  return (
    <section id="contato" data-testid="quote-section" className="relative py-24 sm:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow>04 — Orçamento</Eyebrow></Reveal>
          <h2 className="font-display mt-6 text-3xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
            <MaskLines inView lines={[<>Conte sobre</>, <>o seu <span className="text-grad">projeto.</span></>]} />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-md text-base text-slate-300 md:text-lg">Preencha o formulário e receba um orçamento personalizado. Preço e prazo são definidos de acordo com as necessidades do seu negócio.</p>
            <div className="mt-10 flex flex-col gap-3">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="group inline-flex w-fit items-center gap-3 rounded-full border border-white/15 px-5 py-3 text-sm text-white transition-colors hover:border-[#00C8FF]/60">
                <Instagram className="h-4 w-4 text-[#00C8FF]" /> @jlnexus.digital
              </a>
              {WHATSAPP && (
                <a href={waLink("Olá, JL Nexus!")} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="inline-flex w-fit items-center gap-3 rounded-full border border-white/15 px-5 py-3 text-sm text-white transition-colors hover:border-[#00C8FF]/60">
                  <MessageCircle className="h-4 w-4 text-[#00C8FF]" /> WhatsApp comercial
                </a>
              )}
              <a href="mailto:suporte@jlnexusdigital.com" data-testid="contact-email-link" className="inline-flex w-fit items-center gap-3 rounded-full border border-white/15 px-5 py-3 text-sm text-white transition-colors hover:border-[#00C8FF]/60">
                <Mail className="h-4 w-4 text-[#00C8FF]" /> suporte@jlnexusdigital.com
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} noValidate data-testid="quote-form" className="relative rounded-[1.75rem] border border-white/10 bg-gradient-to-b from-[#141925] to-[#0B0F18] p-6 shadow-[0_40px_80px_-30px_rgba(8,123,255,0.35)] sm:p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field id="nome" label="Nome" error={errors.nome}><input id="nome" autoComplete="name" value={f.nome} onChange={set("nome")} className={cls("nome")} placeholder="Seu nome" data-testid="input-nome" /></Field>
              <Field id="empresa" label="Nome da empresa" error={errors.empresa}><input id="empresa" autoComplete="organization" value={f.empresa} onChange={set("empresa")} className={cls("empresa")} placeholder="Ex.: Barbearia Central" data-testid="input-empresa" /></Field>
              <Field id="telefone" label="Telefone ou WhatsApp" error={errors.telefone}><input id="telefone" type="tel" inputMode="tel" autoComplete="tel" value={f.telefone} onChange={set("telefone")} className={cls("telefone")} placeholder="(11) 91234-5678" data-testid="input-telefone" /></Field>
              <Field id="tipo_negocio" label="Tipo de negócio" error={errors.tipo_negocio}><input id="tipo_negocio" value={f.tipo_negocio} onChange={set("tipo_negocio")} className={cls("tipo_negocio")} placeholder="Ex.: restaurante, oficina, loja" data-testid="input-tipo-negocio" /></Field>
              <div className="sm:col-span-2">
                <Field id="tipo_site" label="Tipo de site desejado" error={errors.tipo_site}>
                  <select id="tipo_site" value={f.tipo_site} onChange={set("tipo_site")} style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2300C8FF' stroke-width='1.6' fill='none'/%3E%3C/svg%3E\")" }} className={`${cls("tipo_site")} appearance-none bg-[length:12px] bg-[right_1.1rem_center] bg-no-repeat pr-10`} data-testid="select-tipo-site">
                    <option value="" disabled>Selecione uma opção</option>
                    {SITE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field id="descricao" label="Descrição do projeto" error={errors.descricao}><textarea id="descricao" rows={4} value={f.descricao} onChange={set("descricao")} className={`${cls("descricao")} resize-none`} placeholder="Conte o que sua empresa faz e o que espera do site." data-testid="input-descricao" /></Field>
              </div>
              <div className="sm:col-span-2">
                <Field id="prazo" label="Prazo desejado" optional><input id="prazo" value={f.prazo} onChange={set("prazo")} className={cls("prazo")} placeholder="Ex.: em até 30 dias" data-testid="input-prazo" /></Field>
              </div>
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" />
            </div>

            <button type="submit" disabled={status === "loading"} data-testid="quote-submit-button" className="btn-shine group mt-8 inline-flex h-14 w-full items-center justify-center gap-3 rounded-full bg-[#087BFF] font-semibold text-white shadow-[0_0_40px_rgba(8,123,255,0.45)] transition-[transform,box-shadow,opacity] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_60px_rgba(0,200,255,0.6)] disabled:opacity-70">
              {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />}
              {status === "loading" ? "Enviando..." : "Solicitar orçamento"}
            </button>

            <AnimatePresence>
              {(status === "success" || status === "error") && (
                <motion.div
                  key={status + msg}
                  role={status === "error" ? "alert" : "status"}
                  data-testid={status === "success" ? "quote-success" : "quote-error"}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`mt-5 flex items-start gap-3 rounded-2xl border p-4 text-sm ${status === "success" ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200" : "border-red-400/30 bg-red-400/10 text-red-200"}`}
                >
                  {status === "success" ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> : <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />}
                  <div>
                    <p>{msg}</p>
                    {status === "success" && wa && (
                      <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="quote-open-whatsapp" className="mt-2 inline-block font-semibold underline underline-offset-4">Abrir conversa no WhatsApp</a>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <p className="mt-5 text-xs text-slate-400">Ao enviar, você concorda com o uso dos seus dados para retorno do orçamento, conforme a <a href="/privacidade" className="underline underline-offset-2 hover:text-white" data-testid="form-privacy-link">Política de Privacidade</a>.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
